import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { Lock, Check, PowerOff, ArrowUpRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  LEVELS,
  POINTS,
  MAX_XP,
} from '@/components/strategy/progress-data';
import {
  VISIBLE_CHANNELS,
  VISIBLE_CHANNEL_GOALS,
  STAGE_META,
  impactFor,
  type ChannelGoal,
  type ChannelStage,
  type StageImpact,
} from '@/components/strategy/progress-channel-goals';
import {
  strategyCompletedGoalsAtom,
  strategyEnabledChannelsAtom,
  strategyKpiTargetsAtom,
  organizationDataAtom,
  type StrategyChannelId,
} from '@/store';
import {
  strategyGoalsStateQueryKey,
  strategyGoalsStateQueryOptions,
} from '@/lib/strategy/query-options';
import {
  putStrategyGoalsState,
  type StrategyGoalsStateUpsert,
  type StrategyGoalsStateWire,
} from '@/lib/strategy/api';
import { cn } from '@/lib/utils';

// The Goals / maturity page. Self-contained (org-scoped state + KPI atoms + grouping) so it can be
// dropped into a container. Lives under the Setup page's "Strategy" tab (#6011); the standalone
// /strategy route redirects here. Wrap in a `key={orgId}` remount for org isolation.

type GoalState = 'completed' | 'available' | 'stage-locked' | 'channel-locked';
type GroupBy = 'channel' | 'stage';

const MS_PER_DAY = 86_400_000;

// Live pacing against a stage's cumulative target day. `daysSinceStart` is
// null when no org start date is known — then only the static target shows.
function stagePacing(
  targetDay: number,
  daysSinceStart: number | null,
  allDone: boolean,
): { label: string; className: string } {
  if (allDone) {
    return { label: 'Complete', className: 'text-[#047857]' };
  }
  if (daysSinceStart === null) {
    return { label: `Target day ${targetDay}`, className: 'text-gray-500' };
  }
  const daysLeft = targetDay - daysSinceStart;
  if (daysLeft > 0) {
    return {
      label: `${daysLeft}d left to day ${targetDay} target`,
      className: 'text-gray-500',
    };
  }
  if (daysLeft === 0) {
    return { label: `Day ${targetDay} target — due today`, className: 'text-[#A16207]' };
  }
  return {
    label: `${-daysLeft}d over day ${targetDay} target`,
    className: 'text-[#B91C1C]',
  };
}

export function GoalsPage() {
  const kpiTargets = useAtomValue(strategyKpiTargetsAtom);
  const organizationData = useAtomValue(organizationDataAtom);
  const [enabledChannels, setEnabledChannels] = useAtom(strategyEnabledChannelsAtom);
  const [completedGoals, setCompletedGoals] = useAtom(strategyCompletedGoalsAtom);
  const [groupBy, setGroupBy] = useState<GroupBy>('stage');

  // Goals-page state persists per organisation. The Goals page is org-level
  // (no Ads account picker), and the selected Ads account atom is frequently
  // null here — scoping by account silently dropped every save. The active
  // org is resolved server-side from the request header.
  const queryClient = useQueryClient();
  const organisationId = organizationData?.id ?? null;

  const { data: goalsStateServer } = useQuery({
    ...strategyGoalsStateQueryOptions(organisationId),
  });

  // Completion is the user's manual checklist. (Auto-verification of Tier A
  // goals against live account state is a separate, out-of-scope feature; the
  // checkbox stays the single source of truth here.)
  const isAutoMet = useMemo(
    () =>
      (_goal: ChannelGoal): boolean =>
        false,
    [],
  );
  const isCompleted = useMemo(
    () =>
      (goal: ChannelGoal): boolean =>
        !!completedGoals[goal.id],
    [completedGoals],
  );

  const saveGoalsState = useMutation({
    mutationFn: (body: StrategyGoalsStateUpsert) => {
      if (organisationId === null) {
        throw new Error('No active organisation selected');
      }
      return putStrategyGoalsState(organisationId, body);
    },
    onSuccess: (data) => {
      queryClient.setQueryData<StrategyGoalsStateWire>(
        strategyGoalsStateQueryKey(data.organisation_id),
        data,
      );
    },
  });

  // Reset the hydration guard when the org changes so the next org's saved
  // state seeds the atoms instead of being skipped.
  const hydratedOrgIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (
      hydratedOrgIdRef.current !== null &&
      hydratedOrgIdRef.current !== organisationId
    ) {
      hydratedOrgIdRef.current = null;
    }
  }, [organisationId]);

  // Hydrate the atoms from the backend once per org. An empty `state` blob
  // means nothing saved yet: keep the atom defaults rather than wiping the
  // UI. Partial blobs merge over the current atom values.
  useEffect(() => {
    if (goalsStateServer === undefined) return;
    if (hydratedOrgIdRef.current === organisationId) return;
    const blob = goalsStateServer.state ?? {};
    if (blob.enabledChannels) {
      setEnabledChannels((prev) => ({ ...prev, ...blob.enabledChannels }));
    }
    if (blob.completedGoals) {
      setCompletedGoals((prev) => ({ ...prev, ...blob.completedGoals }));
    }
    hydratedOrgIdRef.current = organisationId;
  }, [goalsStateServer, organisationId, setEnabledChannels, setCompletedGoals]);

  const persistGoalsState = (
    nextEnabled: Record<string, boolean>,
    nextCompleted: Record<string, boolean>,
  ) => {
    // Mark hydrated so a late-arriving query response can't clobber the
    // user's edit with stale server state.
    hydratedOrgIdRef.current = organisationId;
    saveGoalsState.mutate({
      state: { enabledChannels: nextEnabled, completedGoals: nextCompleted },
    });
  };

  const daysSinceStart = useMemo(() => {
    const createdAt = organizationData?.created_at;
    if (!createdAt) return null;
    const startMs = new Date(createdAt).getTime();
    if (Number.isNaN(startMs)) return null;
    return Math.max(0, Math.floor((Date.now() - startMs) / MS_PER_DAY));
  }, [organizationData?.created_at]);

  const kpiHit = useMemo(
    () =>
      (['cpa', 'roas', 'budget', 'conversions'] as const).filter((k) => kpiTargets[k] > 0)
        .length,
    [kpiTargets],
  );

  const maturityScore = useMemo(() => {
    const goalPoints =
      VISIBLE_CHANNEL_GOALS.filter((g) => isCompleted(g)).length *
      POINTS.CHANNEL_GOAL;
    const kpiPoints = kpiHit * POINTS.KPI_HIT;
    return goalPoints + kpiPoints;
  }, [isCompleted, kpiHit]);

  const currentStage = useMemo(() => {
    const sorted = [...LEVELS].sort((a, b) => a.min - b.min);
    let current = sorted[0];
    for (const tier of sorted) {
      if (maturityScore >= tier.min) current = tier;
    }
    return current.level as ChannelStage;
  }, [maturityScore]);

  const goalState = (goal: ChannelGoal): GoalState => {
    if (!enabledChannels[goal.channel]) return 'channel-locked';
    if (isCompleted(goal)) return 'completed';
    if (goal.stage > currentStage) return 'stage-locked';
    return 'available';
  };

  const toggleChannel = (id: StrategyChannelId, on: boolean) => {
    const next = { ...enabledChannels, [id]: on };
    setEnabledChannels(next);
    persistGoalsState(next, completedGoals);
  };

  const toggleGoal = (goal: ChannelGoal) => {
    const next = { ...completedGoals, [goal.id]: !completedGoals[goal.id] };
    setCompletedGoals(next);
    persistGoalsState(enabledChannels, next);
  };

  const totalCompleted = VISIBLE_CHANNEL_GOALS.filter((g) => isCompleted(g)).length;
  const totalGoals = VISIBLE_CHANNEL_GOALS.length;

  return (
    <div className='space-y-6 p-6'>
      <div className='flex items-start justify-between gap-4 flex-wrap'>
        <div>
          <h1 className='text-2xl font-semibold text-gray-900'>Goals</h1>
          <p className='mt-1 text-sm text-gray-700'>
            {totalCompleted} of {totalGoals} reached. Toggle channels you run; goals at higher
            maturity stages stay locked until the score climbs.
          </p>
          <p className='mt-1 text-xs text-gray-500'>
            Current maturity: Stage {currentStage} · {maturityScore} / {MAX_XP} score
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <GroupByToggle value={groupBy} onChange={setGroupBy} />
        </div>
      </div>

      <Card className='rounded-lg border bg-white shadow-sm'>
        <CardContent className='p-6 space-y-4'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>Channels</h2>
            <p className='text-sm text-gray-500 mt-0.5'>
              Enable the channels you actively run. Disabled channels stay greyed out.
            </p>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3'>
            {VISIBLE_CHANNELS.filter((c) => c.togglable).map((c) => {
              const Icon = c.icon;
              const on = enabledChannels[c.id];
              return (
                <label
                  key={c.id}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                    on ? 'border-[#C4B5FD] bg-white' : 'border-gray-200 bg-gray-50',
                  )}
                >
                  <div
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-full shrink-0',
                      on ? `${c.accentBg} ${c.accentText}` : 'bg-gray-200 text-gray-400',
                    )}
                  >
                    <Icon className='h-4 w-4' />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p
                      className={cn(
                        'text-sm font-semibold truncate',
                        on ? 'text-gray-900' : 'text-gray-500',
                      )}
                    >
                      {c.label}
                    </p>
                    <p className='text-xs text-gray-500 mt-0.5 line-clamp-2'>{c.description}</p>
                  </div>
                  <Switch
                    checked={on}
                    onCheckedChange={(v) => toggleChannel(c.id, v)}
                    className='shrink-0 mt-0.5'
                  />
                </label>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {groupBy === 'channel'
        ? VISIBLE_CHANNELS.map((c) => {
            const channelGoals = VISIBLE_CHANNEL_GOALS.filter((g) => g.channel === c.id);
            const completedCount = channelGoals.filter((g) => isCompleted(g)).length;
            const Icon = c.icon;
            const channelOn = enabledChannels[c.id];
            return (
              <Card key={c.id} className='rounded-lg border bg-white shadow-sm'>
                <CardContent className='p-6 space-y-5'>
                  <div className='flex items-start justify-between gap-4 flex-wrap'>
                    <div className='flex items-center gap-3'>
                      <div
                        className={cn(
                          'flex h-10 w-10 items-center justify-center rounded-full',
                          channelOn ? `${c.accentBg} ${c.accentText}` : 'bg-gray-200 text-gray-400',
                        )}
                      >
                        <Icon className='h-5 w-5' />
                      </div>
                      <div>
                        <h2 className='text-lg font-semibold text-gray-900'>{c.label}</h2>
                        <p className='text-sm text-gray-500 mt-0.5'>
                          {completedCount} of {channelGoals.length} reached
                          {!channelOn ? ' · channel disabled' : ''}
                        </p>
                      </div>
                    </div>
                    {c.togglable ? (
                      <Switch
                        checked={channelOn}
                        onCheckedChange={(v) => toggleChannel(c.id, v)}
                      />
                    ) : null}
                  </div>

                  {channelOn ? (
                    <ChannelStageList
                      channelId={c.id}
                      channelGoals={channelGoals}
                      currentStage={currentStage}
                      channelOn={channelOn}
                      goalState={goalState}
                      toggleGoal={toggleGoal}
                      isAutoMet={isAutoMet}
                    />
                  ) : (
                    <Accordion type='single' collapsible>
                      <AccordionItem value={`channel-${c.id}`} className='border-b-0'>
                        <AccordionTrigger className='py-2 hover:no-underline text-sm font-medium text-gray-700'>
                          Show {channelGoals.length} locked goals
                        </AccordionTrigger>
                        <AccordionContent>
                          <ChannelStageList
                            channelId={c.id}
                            channelGoals={channelGoals}
                            currentStage={currentStage}
                            channelOn={channelOn}
                            goalState={goalState}
                            toggleGoal={toggleGoal}
                            isAutoMet={isAutoMet}
                          />
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </CardContent>
              </Card>
            );
          })
        : ([0, 1, 2, 3, 4, 5] as ChannelStage[]).map((stage) => {
            const stageGoals = VISIBLE_CHANNEL_GOALS.filter((g) => g.stage === stage);
            if (stageGoals.length === 0) return null;
            const stageMeta = LEVELS.find((l) => l.level === stage);
            const stageMetaInfo = STAGE_META[stage];
            const stageTitle =
              stage === 0 ? stageMetaInfo.phase : (stageMeta?.title ?? `Stage ${stage}`);
            const completedCount = stageGoals.filter((g) => isCompleted(g)).length;
            const stageReached = stage <= currentStage;
            const pacing = stagePacing(
              stageMetaInfo.targetDay,
              daysSinceStart,
              completedCount === stageGoals.length,
            );
            return (
              <Card key={`stage-${stage}`} className='rounded-lg border bg-white shadow-sm'>
                <CardContent className='p-6 space-y-5'>
                  <div className='flex items-start gap-3 flex-wrap'>
                    <span
                      className={cn(
                        'inline-flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold shrink-0',
                        stageReached ? 'bg-[#290BD9] text-white' : 'bg-gray-200 text-gray-500',
                      )}
                    >
                      {stage === 0 ? 'F' : `S${stage}`}
                    </span>
                    <div className='flex-1 min-w-[200px] space-y-2'>
                      <div>
                        <h2 className='text-lg font-semibold text-gray-900'>{stageTitle}</h2>
                        <p className='text-sm text-gray-500 mt-0.5'>
                          {stageMetaInfo.phase !== stageTitle && (
                            <>{stageMetaInfo.phase} · </>
                          )}
                          <span className={pacing.className}>{pacing.label}</span> ·{' '}
                          {completedCount} of {stageGoals.length} reached
                        </p>
                      </div>
                      <div className='space-y-1'>
                        <p className='text-[11px] uppercase tracking-wide text-gray-500 font-semibold'>
                          Rolled-up benefit at this stage
                        </p>
                        <StageImpactRow impact={stageMetaInfo.rolledUp} />
                      </div>
                    </div>
                  </div>
                  <StageChannelList
                    stageGoals={stageGoals}
                    goalState={goalState}
                    toggleGoal={toggleGoal}
                    isAutoMet={isAutoMet}
                    requiredStageTitle={stageTitle}
                    stage={stage}
                  />
                </CardContent>
              </Card>
            );
          })}
    </div>
  );
}

interface GroupByToggleProps {
  value: GroupBy;
  onChange: (next: GroupBy) => void;
}

function GroupByToggle({ value, onChange }: GroupByToggleProps) {
  return (
    <div className='inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5 text-sm'>
      {(['channel', 'stage'] as const).map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type='button'
            onClick={() => onChange(opt)}
            className={cn(
              'px-3 py-1.5 rounded-[5px] font-medium transition-colors',
              active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
            )}
          >
            By {opt}
          </button>
        );
      })}
    </div>
  );
}

interface StageImpactRowProps {
  impact: StageImpact | null;
}

function StageImpactRow({ impact }: StageImpactRowProps) {
  if (!impact) return null;
  if (impact.note) {
    return (
      <p className='text-[11px] uppercase tracking-wide text-gray-400 font-semibold'>
        {impact.note}
      </p>
    );
  }
  return (
    <div className='flex flex-wrap items-center gap-1.5'>
      <ImpactBadge label='ROAS' value={impact.roas} />
      {impact.conversions ? <ImpactBadge label='Conv' value={impact.conversions} /> : null}
      {impact.cpa ? <ImpactBadge label='CPA' value={impact.cpa} /> : null}
      <ImpactBadge label='Spend' value={impact.spend} />
    </div>
  );
}

interface ImpactBadgeProps {
  label: string;
  value: string;
}

function ImpactBadge({ label, value }: ImpactBadgeProps) {
  return (
    <span className='inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-700'>
      <span className='uppercase tracking-wide text-gray-500'>{label}</span>
      <span className='text-gray-900'>{value}</span>
    </span>
  );
}

interface StageChannelListProps {
  stageGoals: ChannelGoal[];
  goalState: (goal: ChannelGoal) => GoalState;
  toggleGoal: (goal: ChannelGoal) => void;
  isAutoMet: (goal: ChannelGoal) => boolean;
  requiredStageTitle: string;
  stage: ChannelStage;
}

function StageChannelList({
  stageGoals,
  goalState,
  toggleGoal,
  isAutoMet,
  requiredStageTitle,
  stage,
}: StageChannelListProps) {
  return (
    <div className='space-y-5'>
      {VISIBLE_CHANNELS.map((c) => {
        const goalsForChannel = stageGoals.filter((g) => g.channel === c.id);
        if (goalsForChannel.length === 0) return null;
        const Icon = c.icon;
        const impact = impactFor(c.id, stage);
        return (
          <div key={c.id} className='space-y-2'>
            <div className='flex items-center gap-2 flex-wrap'>
              <span
                className={cn(
                  'inline-flex items-center justify-center rounded-full h-6 w-6',
                  c.accentBg,
                  c.accentText,
                )}
              >
                <Icon className='h-3 w-3' />
              </span>
              <p className='text-xs uppercase tracking-wide text-gray-500 font-semibold'>
                {c.label}
              </p>
              <StageImpactRow impact={impact} />
            </div>
            <ul className='grid grid-cols-1 md:grid-cols-2 gap-2'>
              {goalsForChannel.map((goal) => (
                <GoalRow
                  key={goal.id}
                  goal={goal}
                  state={goalState(goal)}
                  autoMet={isAutoMet(goal)}
                  onToggle={() => toggleGoal(goal)}
                  requiredStageTitle={requiredStageTitle}
                />
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

interface ChannelStageListProps {
  channelId: StrategyChannelId;
  channelGoals: ChannelGoal[];
  currentStage: ChannelStage;
  channelOn: boolean;
  goalState: (goal: ChannelGoal) => GoalState;
  toggleGoal: (goal: ChannelGoal) => void;
  isAutoMet: (goal: ChannelGoal) => boolean;
}

function ChannelStageList({
  channelId,
  channelGoals,
  currentStage,
  channelOn,
  goalState,
  toggleGoal,
  isAutoMet,
}: ChannelStageListProps) {
  return (
    <div className='space-y-5'>
      {([0, 1, 2, 3, 4, 5] as ChannelStage[]).map((stage) => {
        const stageGoals = channelGoals.filter((g) => g.stage === stage);
        if (stageGoals.length === 0) return null;
        const stageTitle =
          stage === 0
            ? STAGE_META[0].phase
            : (LEVELS.find((l) => l.level === stage)?.title ?? `Stage ${stage}`);
        const impact = impactFor(channelId, stage);
        return (
          <div key={stage} className='space-y-2'>
            <div className='flex items-center gap-2 flex-wrap'>
              <span
                className={cn(
                  'inline-flex items-center justify-center rounded-full h-6 w-6 text-xs font-semibold',
                  stage <= currentStage && channelOn
                    ? 'bg-[#290BD9] text-white'
                    : 'bg-gray-200 text-gray-500',
                )}
              >
                {stage === 0 ? 'F' : `S${stage}`}
              </span>
              <p className='text-xs uppercase tracking-wide text-gray-500 font-semibold'>
                {stageTitle}
              </p>
              <StageImpactRow impact={impact} />
            </div>
            <ul className='grid grid-cols-1 md:grid-cols-2 gap-2'>
              {stageGoals.map((goal) => (
                <GoalRow
                  key={goal.id}
                  goal={goal}
                  state={goalState(goal)}
                  autoMet={isAutoMet(goal)}
                  onToggle={() => toggleGoal(goal)}
                  requiredStageTitle={stageTitle}
                />
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

interface GoalRowProps {
  goal: ChannelGoal;
  state: GoalState;
  autoMet: boolean;
  onToggle: () => void;
  requiredStageTitle: string;
}

function GoalRow({ goal, state, autoMet, onToggle, requiredStageTitle }: GoalRowProps) {
  const completed = state === 'completed';
  const stageLocked = state === 'stage-locked';
  const channelLocked = state === 'channel-locked';
  const interactive = !autoMet && (state === 'available' || state === 'completed');
  const showRouteLink = !!goal.route && !channelLocked;
  return (
    <li className='relative'>
      <button
        type='button'
        onClick={interactive ? onToggle : undefined}
        disabled={!interactive}
        className={cn(
          'flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
          showRouteLink && 'pr-9',
          completed && 'border-[#C4B5FD] bg-[#EDE9FE]',
          state === 'available' && 'border-gray-200 bg-white hover:border-[#C4B5FD] hover:bg-[#F5F3FF] cursor-pointer',
          stageLocked && 'border-dashed border-gray-300 bg-gray-50 cursor-not-allowed',
          channelLocked && 'border-dashed border-gray-200 bg-gray-50 cursor-not-allowed opacity-60',
        )}
      >
        <span
          className={cn(
            'flex h-5 w-5 items-center justify-center rounded-full shrink-0 mt-0.5',
            completed
              ? 'bg-[#290BD9] text-white'
              : state === 'available'
                ? 'border border-gray-300 bg-white'
                : 'bg-gray-200 text-gray-400',
          )}
        >
          {completed ? (
            <Check className='h-3 w-3' />
          ) : stageLocked ? (
            <Lock className='h-3 w-3' />
          ) : channelLocked ? (
            <PowerOff className='h-3 w-3' />
          ) : null}
        </span>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2 flex-wrap'>
            <p
              className={cn(
                'text-sm font-semibold',
                completed
                  ? 'text-gray-900'
                  : state === 'available'
                    ? 'text-gray-900'
                    : 'text-gray-500',
              )}
            >
              {goal.title}
            </p>
          </div>
          <p className='text-xs text-gray-500 mt-0.5'>{goal.description}</p>
          {stageLocked ? (
            <p className='text-[11px] uppercase tracking-wide text-gray-400 font-semibold mt-1'>
              Reach Stage {goal.stage} · {requiredStageTitle} to unlock
            </p>
          ) : null}
          {channelLocked ? (
            <p className='text-[11px] uppercase tracking-wide text-gray-400 font-semibold mt-1'>
              Enable channel to track this goal
            </p>
          ) : null}
        </div>
      </button>
      {showRouteLink ? (
        <Link
          to={goal.route!}
          hash={goal.hash}
          aria-label={`Open ${goal.title}`}
          className='absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-[#290BD9] hover:bg-[#EDE9FE] hover:text-[#2309C5]'
        >
          <ArrowUpRight className='h-4 w-4' />
        </Link>
      ) : null}
    </li>
  );
}
